/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable array-callback-return */
import React, { useCallback, useState } from 'react';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { COLOR_PRIMARY, COLOR_SECONDARY } from 'styles/theme';
import { withStyles } from '@material-ui/core';
import { useEnumColumnData } from 'modules/common/enumTableColumn';
import { ClientAddressEditRecordModal, ClientAddressCreateRecordModal } from './clientAddressManagementModals.component';

const styles = theme => ({
  editModalContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100% !important',
    marginLeft: '0px !important',
  },
  editModalColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100% !important',
  },
  editModalInput: {
    minWidth: '332px',
  },
  dialogContentContainer: {
    margin: '15px',
  }
});

const theme = createTheme({
  palette: {
    primary: {
      main: COLOR_PRIMARY.main,
    },
    secondary: {
      main: COLOR_SECONDARY.main,
    },
  },
});

/**
 * Custom Client Address Management Table with Google Places Autocomplete
 */
const ClientAddressManagementTable = ({
  classes,
  columns,
  rows,
  createRow,
  updateRow,
  deleteRow,
  customAddText,
  hideEdit,
  countriesEnum,
  provincesEnum,
  isLoading = false,
}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editingModalOpen, setEditingModalOpen] = useState(false);
  const [tableData, setTableData] = useState(() => rows);

  // Update table data when rows prop changes
  React.useEffect(() => {
    setTableData(rows);
  }, [rows]);

  const data = useEnumColumnData(tableData, columns);

  const handleCreateNewRow = async (values) => {
    const result = await createRow(values);
    if (result !== false) {
      setCreateModalOpen(false);
    }
  };

  const handleSaveRowEdits = async (values, row) => {
    const result = await updateRow(row, values);
    if (result !== false) {
      setEditingModalOpen(false);
      setEditingRow(null);
    }
  };

  const handleDeleteRow = useCallback(
    async (row) => {
      if (!window.confirm(`Are you sure you want to delete this address?`)) {
        return;
      }
      await deleteRow(row);
    },
    [deleteRow],
  );

  const handleEditRow = (row) => {
    setEditingRow(row);
    setEditingModalOpen(true);
  };

  const hideColumns = columns => {
    let hiddenCols = {};
    columns.map((col) => {
      if (col.hideInTable) {
        hiddenCols[col.accessorKey] = false;
      }
    });
    return hiddenCols;
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
            margin: 1,
            overflow: 'hidden',
        }}
      >
        <MaterialReactTable
            displayColumnDefOptions={{
            'mrt-row-actions': {
                muiTableHeadCellProps: {
                align: 'center',
                },
                size: 120,
            },
            }}
            columns={columns}
            data={data}
            editingMode="modal"
            enableColumnOrdering={false}
            enableEditing={!hideEdit}
            enableSorting={false}
            enablePagination={false}
            enableBottomToolbar={false}
            enableColumnActions={false}
            positionActionsColumn="first"
            initialState={{ columnVisibility: hideColumns(columns) }}
            renderRowActions={({ row, table }) => (
            <Box sx={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                {!hideEdit && (
                <Tooltip arrow placement="left" title="Edit">
                    <IconButton onClick={() => handleEditRow(row)}>
                    <Edit />
                    </IconButton>
                </Tooltip>
                )}
                {!hideEdit && (
                <Tooltip arrow placement="right" title="Delete">
                    <IconButton color="error" onClick={() => handleDeleteRow(row)}>
                    <Delete />
                    </IconButton>
                </Tooltip>
                )}
            </Box>
            )}
            renderTopToolbarCustomActions={() => (
            <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                {!hideEdit && (
                <Button
                    color="primary"
                    onClick={() => setCreateModalOpen(true)}
                    variant="contained"
                    sx={{ color: 'white' }}
                >
                    {customAddText || 'Add Record'}
                </Button>
                )}
            </Box>
            )}
        />
      </Box>
      {createModalOpen && (
        <ClientAddressCreateRecordModal
          columns={columns}
          classes={classes}
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSubmit={handleCreateNewRow}
          isLoading={isLoading}
          countriesEnum={countriesEnum}
          provincesEnum={provincesEnum}
        />
      )}
      {editingModalOpen && editingRow && (
        <ClientAddressEditRecordModal
          columns={columns}
          classes={classes}
          open={editingModalOpen}
          onClose={() => {
            setEditingModalOpen(false);
            setEditingRow(null);
          }}
          row={editingRow}
          onSubmit={handleSaveRowEdits}
          isLoading={isLoading}
          hideEdit={hideEdit}
          countriesEnum={countriesEnum}
          provincesEnum={provincesEnum}
        />
      )}
    </ThemeProvider>
  );
};

export default withStyles(styles)(ClientAddressManagementTable);
